import api, { API } from "@/lib/api";

/**
 * Converts a base64 data URL string into a File object.
 */
export function dataURLtoFile(dataurl, filename) {
  try {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch {
    return null;
  }
}

/**
 * Converts Markdown string into clean HTML for contentEditable canvas initialization.
 */
export function markdownToHtml(md) {
  if (!md) return "";
  const token = localStorage.getItem("ft_token") || "";

  let text = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Images: ![alt](url)
  text = text.replace(/!\[([^\]]*)\]\(([^\)]+)\)/g, (match, alt, url) => {
    let src = url;
    if (src.startsWith("/api/")) {
      src = `${API}${src.substring(4)}`;
    } else if (src.startsWith("api/")) {
      src = `${API}${src.substring(3)}`;
    }
    if (src.includes("/attachments/") && src.includes("/download") && token) {
      if (src.includes("auth=")) {
        src = src.replace(/auth=[^&]+/, `auth=${encodeURIComponent(token)}`);
      } else {
        src += (src.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(token);
      }
    }
    return `<div class="my-3 inline-block max-w-full"><img src="${src}" alt="${alt || 'Image'}" class="max-w-md max-h-[400px] rounded-lg border border-border shadow-sm object-contain my-1" /></div>`;
  });

  // Code blocks: ```lang ... ```
  text = text.replace(/```([a-z]*)\n([\s\S]*?)```/g, (match, lang, code) => {
    return `<pre class="bg-slate-900 text-slate-100 p-3 rounded-md font-mono text-xs my-2"><code>${code.trim()}</code></pre>`;
  });

  // Callouts :::info, :::warning, :::success
  text = text.replace(/:::(info|warning|success|note|danger)\n([\s\S]*?):::/g, (match, type, content) => {
    return `<div class="p-3 my-2 rounded-md border text-xs font-medium bg-muted/40">${content.trim()}</div>`;
  });

  // Convert Markdown Tables into visual HTML <table>
  const tableRegex = /(?:\|[^\n]+\|\n?)+/g;
  text = text.replace(tableRegex, (tableBlock) => {
    const rawRows = tableBlock.trim().split('\n').filter(r => r.trim().startsWith('|'));
    if (rawRows.length < 1) return tableBlock;

    let htmlTable = '<div class="overflow-x-auto my-3"><table class="w-full border-collapse border border-border rounded-lg text-sm bg-background shadow-xs"><thead>';
    let inBody = false;

    rawRows.forEach((rowStr, idx) => {
      // Check if separator row like | --- | --- |
      if (rowStr.replace(/[\s|:-]/g, '').length === 0) {
        if (!inBody) {
          htmlTable += '</thead><tbody>';
          inBody = true;
        }
        return;
      }

      const cells = rowStr.split('|').slice(1, -1).map(c => c.trim());
      const isHeader = !inBody && idx === 0;
      const cellTag = isHeader ? 'th' : 'td';
      const cellClass = isHeader 
        ? 'border border-border px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider bg-muted/60 text-muted-foreground' 
        : 'border border-border px-3 py-2 text-sm';

      htmlTable += '<tr>';
      cells.forEach(cell => {
        htmlTable += `<${cellTag} class="${cellClass}">${cell}</${cellTag}>`;
      });
      htmlTable += '</tr>';
    });

    if (inBody) {
      htmlTable += '</tbody>';
    } else {
      htmlTable += '</thead>';
    }
    htmlTable += '</table></div>';
    return htmlTable;
  });

  // Headings
  text = text.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  text = text.replace(/^## (.*$)/gim, '<h2>$2</h2>');
  text = text.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold & Italic & Strikethrough & Code
  text = text.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
  text = text.replace(/\*(.*?)\*/g, '<i>$1</i>');
  text = text.replace(/~~(.*?)~~/g, '<del>$1</del>');
  text = text.replace(/`(.*?)`/g, '<code class="bg-muted px-1 rounded text-xs">$1</code>');

  // Lists and paragraphs
  const lines = text.split('\n');
  let inUl = false;
  let inOl = false;
  const processedLines = [];

  for (let line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('<div') || trimmed.startsWith('<table') || trimmed.startsWith('<thead>') || trimmed.startsWith('<tbody>') || trimmed.startsWith('<tr>') || trimmed.startsWith('</th>') || trimmed.startsWith('</td>') || trimmed.startsWith('</table')) {
      if (inUl) { processedLines.push('</ul>'); inUl = false; }
      if (inOl) { processedLines.push('</ol>'); inOl = false; }
      processedLines.push(line);
    } else if (trimmed.startsWith('- ')) {
      if (!inUl) { processedLines.push('<ul>'); inUl = true; }
      processedLines.push(`<li>${trimmed.substring(2)}</li>`);
    } else if (trimmed.match(/^\d+\.\s/)) {
      if (!inOl) { processedLines.push('<ol>'); inOl = true; }
      processedLines.push(`<li>${trimmed.replace(/^\d+\.\s/, '')}</li>`);
    } else {
      if (inUl) { processedLines.push('</ul>'); inUl = false; }
      if (inOl) { processedLines.push('</ol>'); inOl = false; }
      if (trimmed.length > 0 && !trimmed.startsWith('<h') && !trimmed.startsWith('<div') && !trimmed.startsWith('<pre')) {
        processedLines.push(`<p>${line}</p>`);
      } else {
        processedLines.push(line);
      }
    }
  }
  if (inUl) processedLines.push('</ul>');
  if (inOl) processedLines.push('</ol>');

  return processedLines.join('\n');
}

/**
 * Converts tab-separated Excel text into a clean HTML table.
 */
export function excelTextToHtmlTable(text) {
  if (!text || !text.includes('\t')) return null;
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length === 0) return null;

  let htmlTable = '<div class="overflow-x-auto my-3"><table class="w-full border-collapse border border-border rounded-lg text-sm bg-background shadow-xs"><thead>';
  
  lines.forEach((line, idx) => {
    const cells = line.split('\t').map(c => c.trim());
    const isHeader = idx === 0;
    const tag = isHeader ? 'th' : 'td';
    const cls = isHeader
      ? 'border border-border px-3 py-2 text-left font-semibold text-xs uppercase tracking-wider bg-muted/60 text-muted-foreground'
      : 'border border-border px-3 py-2 text-sm';

    if (idx === 1) htmlTable += '</thead><tbody>';

    htmlTable += '<tr>';
    cells.forEach(cell => {
      htmlTable += `<${tag} class="${cls}">${cell || '&nbsp;'}</${tag}>`;
    });
    htmlTable += '</tr>';
  });

  if (lines.length > 1) htmlTable += '</tbody>';
  else htmlTable += '</thead>';
  htmlTable += '</table></div>';
  return htmlTable;
}

/**
 * Converts HTML string (e.g. copied from Confluence / Word / Web or contentEditable DOM) into clean Markdown tables, images & formatted blocks.
 */
export function htmlToMarkdown(htmlString) {
  if (!htmlString) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Remove non-content style, script, xml, head, meta, link tags (prevents Excel/Word CSS rules from appearing as text)
    doc.querySelectorAll("style, script, xml, head, meta, link, title").forEach(el => el.remove());

    function parseNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Ignore text inside ignored tags if any remained
        const parentTag = node.parentNode?.tagName?.toLowerCase();
        if (["style", "script", "xml", "head", "meta", "link"].includes(parentTag)) return "";
        return node.textContent;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return "";

      const tag = node.tagName.toLowerCase();

      // Ignore style & metadata elements completely
      if (["style", "script", "xml", "head", "meta", "link", "title"].includes(tag)) {
        return "";
      }

      const children = Array.from(node.childNodes).map(parseNode).join("");

      switch (tag) {
        case "h1": return `\n\n# ${children.trim()}\n\n`;
        case "h2": return `\n\n## ${children.trim()}\n\n`;
        case "h3": return `\n\n### ${children.trim()}\n\n`;
        case "h4": return `\n\n#### ${children.trim()}\n\n`;
        case "p": return `\n\n${children.trim()}\n\n`;
        case "strong":
        case "b": return `**${children.trim()}**`;
        case "em":
        case "i": return `*${children.trim()}*`;
        case "code": return `\`${children.trim()}\``;
        case "pre": return `\n\n\`\`\`\n${children.trim()}\n\`\`\`\n\n`;
        case "blockquote": return `\n\n> ${children.trim()}\n\n`;
        case "ul":
        case "ol": return `\n\n${children}\n\n`;
        case "li": return `- ${children.trim()}\n`;
        case "hr": return `\n\n---\n\n`;
        case "a": {
          const href = node.getAttribute("href") || "#";
          return `[${children.trim()}](${href})`;
        }
        case "img": {
          let src = node.getAttribute("src") || node.getAttribute("data-image-src") || node.getAttribute("data-src") || "";
          const alt = node.getAttribute("alt") || node.getAttribute("data-media-id") || "Image";
          if (!src) return "";
          const token = localStorage.getItem("ft_token") || "";
          if (src.startsWith("/api/")) {
            src = `${API}${src.substring(4)}`;
          } else if (src.startsWith("api/")) {
            src = `${API}${src.substring(3)}`;
          }
          if (src.includes("/attachments/") && src.includes("/download") && token) {
            if (src.includes("auth=")) {
              src = src.replace(/auth=[^&]+/, `auth=${encodeURIComponent(token)}`);
            } else {
              src += (src.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(token);
            }
          }
          return `\n\n![${alt}](${src})\n\n`;
        }
        case "table": {
          const rows = Array.from(node.querySelectorAll("tr"));
          if (!rows.length) return "";
          let mdTable = "\n\n";

          rows.forEach((row, rowIndex) => {
            const cells = Array.from(row.querySelectorAll("th, td"));
            if (!cells.length) return;
            const cellTexts = cells.map((c, cIdx) => {
              const text = parseNode(c).replace(/\s+/g, " ").trim().replace(/\|/g, "\\|");
              if (!text) {
                return rowIndex === 0 ? `Column ${cIdx + 1}` : "&nbsp;";
              }
              return text;
            });
            mdTable += `| ${cellTexts.join(" | ")} |\n`;

            if (rowIndex === 0) {
              const separators = Array(cellTexts.length).fill("---");
              mdTable += `| ${separators.join(" | ")} |\n`;
            }
          });
          return mdTable + "\n\n";
        }
        default:
          return children;
      }
    }

    let result = parseNode(doc.body);

    // Clean up residual MSO css rules or garbage text lines from Word / Excel paste
    result = result
      .replace(/\{[^\}]*mso-[^\}]*\}/gi, "")
      .replace(/mso-[^;]+;/gi, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    return result;
  } catch (err) {
    console.error("Failed to parse HTML to markdown:", err);
    return "";
  }
}

/**
 * Uploads an image file to /api/attachments/upload and returns the formatted Markdown image snippet or URL.
 */
export async function uploadImageAttachment(file, issueId = null, pageId = null) {
  const fd = new FormData();
  fd.append("file", file);
  if (issueId) fd.append("issue_id", issueId);
  if (pageId) fd.append("page_id", pageId);

  const res = await api.post("/attachments/upload", fd);
  const token = localStorage.getItem("ft_token") || "";
  const imgUrl = `${API}/attachments/${res.data.id}/download?auth=${encodeURIComponent(token)}`;
  return {
    id: res.data.id,
    filename: res.data.original_filename || file.name || "Image",
    url: imgUrl,
    snippet: `\n![${res.data.original_filename || file.name || "Image"}](${imgUrl})\n`,
  };
}

/**
 * Processes markdown text and replaces any embedded base64 images with uploaded attachment download URLs.
 */
export async function processBase64ImagesInMarkdown(mdText, issueId = null, pageId = null) {
  if (!mdText) return mdText;
  const base64Regex = /!\[([^\]]*)\]\((data:image\/[a-zA-Z]+;base64,[^\)]+)\)/g;
  let match;
  let nextContent = mdText;

  const matches = [];
  while ((match = base64Regex.exec(mdText)) !== null) {
    matches.push({ full: match[0], alt: match[1], dataUrl: match[2] });
  }

  if (matches.length > 0) {
    for (const m of matches) {
      const file = dataURLtoFile(m.dataUrl, `${(m.alt || "pasted_image").replace(/\s+/g, "_")}.png`);
      if (file) {
        try {
          const result = await uploadImageAttachment(file, issueId, pageId);
          nextContent = nextContent.replace(m.dataUrl, result.url);
        } catch (e) {
          console.error("Failed to upload base64 image", e);
        }
      }
    }
  }

  return nextContent;
}
